import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';

import { Badge, Button, Skeleton } from '@workspace/shadcn';

import { useCourses } from '../../lib/api.ts';
import { usePageMeta } from '../../lib/seo.tsx';
import { CourseCard } from '../components/course-card.tsx';
import {
  CourseMeta,
  type CourseCardData,
} from '../components/course-meta.tsx';
import { useModals } from '../components/modals.tsx';
import { Section } from '../components/section.tsx';
import { BackgroundLines } from '../components/effects/background-lines.tsx';

function FeaturedCourse({ course }: { course: CourseCardData }) {
  return (
    <article className="border-foreground/10 bg-card group relative overflow-hidden rounded-2xl border transition duration-200 hover:shadow-xl">
      <div className="grid grid-cols-12 items-center gap-6">
        <div className="col-span-12 lg:col-span-7">
          <div className="relative aspect-16/10 w-full overflow-hidden rounded-t-2xl">
            <img
              loading="eager"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
              src={course.imageUrl}
              alt={course.title}
            />
            <div className="absolute start-4 top-4 z-10">
              <Badge className="bg-primary text-primary-foreground">
                Featured
              </Badge>
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <div className="p-6">
            {course.category && (
              <Badge className="bg-foreground text-background mb-2">
                {course.category}
              </Badge>
            )}
            <h2 className="mb-2 text-2xl font-bold md:text-3xl">
              {course.title}
            </h2>
            <p className="text-muted-foreground mb-4">{course.oneLiner}</p>

            <CourseMeta course={course} />

            <Link to={`/courses/${course.slug}`}>
              <Button className="bg-foreground/95 text-background hover:bg-foreground group/button w-full gap-1">
                Learn More
                <ArrowRight className="h-4 w-4 transition-transform group-hover/button:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Courses() {
  const { data, isLoading, isError } = useCourses();
  const { open } = useModals();

  usePageMeta({
    title: 'ITGL Academy | All Courses',
    description:
      'Browse our diverse range of cutting-edge courses designed for the MENA region healthcare demands.',
    pathname: '/courses',
  });

  const records = data?.records ?? [];
  const featured = records.filter((course) => course.featured);
  const rest = records.filter((course) => !course.featured);

  return (
    <Section className="bg-background text-foreground overflow-hidden px-6 lg:px-10">
      <div className="relative mt-16 grid grid-cols-12 justify-center">
        <div className="relative col-span-12 h-fit text-center lg:col-span-8 lg:col-start-3">
          <div className="absolute -top-80 left-0 -z-10 h-screen w-full">
            <BackgroundLines />
          </div>
          <h1 className="mb-4 text-3xl leading-tight font-bold lg:text-5xl">
            Explore Our Courses
          </h1>
          <p className="text-muted-foreground mb-9 text-lg font-medium lg:px-12">
            Browse our diverse range of cutting-edge courses designed for the
            MENA region healthcare demands.
          </p>
        </div>
      </div>

      {isError ? (
        <p className="text-muted-foreground mt-10 text-center">
          Our courses are taking a moment to load. Please refresh the page.
        </p>
      ) : isLoading ? (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 md:mt-14 lg:mt-20 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-[420px] rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-12 items-stretch gap-2 sm:gap-8 md:mt-14 lg:mt-20">
          {featured.map((course) => (
            <div key={course.slug} className="col-span-12">
              <FeaturedCourse course={course} />
            </div>
          ))}
          {rest.map((course) => (
            <div
              key={course.slug}
              className="col-span-12 md:col-span-6 lg:col-span-4"
            >
              <CourseCard course={course} />
            </div>
          ))}
        </div>
      )}

      <div className="bg-primary/10 mx-auto mt-16 max-w-4xl overflow-hidden rounded-2xl border px-6 py-8 text-center lg:px-10">
        <h2 className="mb-4 text-3xl leading-tight font-bold lg:text-4xl">
          More Courses Coming Soon!
        </h2>
        <p className="text-muted-foreground mb-4 text-lg font-medium lg:px-12">
          We are continuously expanding our course catalog to provide you with
          the latest insights and skills in healthcare. Whether you are
          enhancing your expertise or exploring new trends, exciting new courses
          are on the way.
        </p>
        <Button
          className="h-12 px-8 text-base text-white dark:text-black"
          onClick={() => open('suggestion')}
        >
          Suggest a Course
        </Button>
      </div>
    </Section>
  );
}
